import React, { useState, useEffect, useContext } from 'react';
import { Navigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';
import './AdminPage.css';

function AdminPage() {
  const { isAuthenticated } = useContext(AuthContext);
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('list'); // 'list', 'create', 'edit'
  const [editingPage, setEditingPage] = useState(null);
  const [formData, setFormData] = useState({
    slug: '',
    title: '',
    content: '',
    is_published: true
  });

  useEffect(() => {
    if (isAuthenticated) {
      fetchPages();
    }
  }, [isAuthenticated]);

  const fetchPages = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/v1/pages/', {
        params: { include_unpublished: true }
      });
      setPages(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching pages:', err);
      setError('Failed to load pages. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const resetForm = () => {
    setFormData({
      slug: '',
      title: '',
      content: '',
      is_published: true
    });
    setEditingPage(null);
  };

  const handleCreatePage = async (e) => {
    e.preventDefault();
    try {
      await axios.post('/api/v1/pages/', formData);
      resetForm();
      fetchPages();
      setActiveTab('list');
    } catch (err) {
      console.error('Error creating page:', err);
      setError('Failed to create page. ' + (err.response?.data?.detail || 'Please try again.'));
    }
  };

  const handleUpdatePage = async (e) => {
    e.preventDefault();
    try {
      const { title, content, is_published } = formData;
      await axios.put(`/api/v1/pages/${editingPage.slug}`, {
        title,
        content,
        is_published
      });
      resetForm();
      fetchPages();
      setActiveTab('list');
    } catch (err) {
      console.error('Error updating page:', err);
      setError('Failed to update page. ' + (err.response?.data?.detail || 'Please try again.'));
    }
  };

  const handleDeletePage = async (slug) => {
    if (window.confirm('Are you sure you want to delete this page?')) {
      try {
        await axios.delete(`/api/v1/pages/${slug}`);
        fetchPages();
      } catch (err) {
        console.error('Error deleting page:', err);
        setError('Failed to delete page. ' + (err.response?.data?.detail || 'Please try again.'));
      }
    }
  };

  const startEditing = (page) => {
    setEditingPage(page);
    setFormData({
      slug: page.slug,
      title: page.title,
      content: page.content,
      is_published: page.is_published
    });
    setActiveTab('edit');
  };

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  return (
    <div className="admin-page">
      <div className="container">
        <h1>Admin Dashboard</h1>
        
        <div className="admin-tabs">
          <button 
            className={activeTab === 'list' ? 'active' : ''} 
            onClick={() => setActiveTab('list')}
          >
            Pages List
          </button>
          <button 
            className={activeTab === 'create' ? 'active' : ''} 
            onClick={() => {
              resetForm();
              setActiveTab('create');
            }}
          >
            Create New Page
          </button>
        </div>

        {error && <div className="error-message">{error}</div>}

        {activeTab === 'list' && (
          <div className="pages-list">
            <h2>Custom Pages</h2>
            {loading ? (
              <p>Loading pages...</p>
            ) : pages.length === 0 ? (
              <p>No pages found. Create your first page!</p>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Slug</th>
                    <th>Title</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pages.map(page => (
                    <tr key={page.id}>
                      <td>{page.slug}</td>
                      <td>{page.title}</td>
                      <td>{page.is_published ? 'Published' : 'Draft'}</td>
                      <td>
                        <div className="action-buttons">
                          <button 
                            className="view-btn"
                            onClick={() => window.open(`/p/${page.slug}`, '_blank')}
                          >
                            View
                          </button>
                          <button 
                            className="edit-btn"
                            onClick={() => startEditing(page)}
                          >
                            Edit
                          </button>
                          <button 
                            className="delete-btn"
                            onClick={() => handleDeletePage(page.slug)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {activeTab === 'create' && (
          <div className="page-form">
            <h2>Create New Page</h2>
            <form onSubmit={handleCreatePage}>
              <div className="form-group">
                <label htmlFor="slug">Slug (URL path):</label>
                <input
                  type="text"
                  id="slug"
                  name="slug"
                  value={formData.slug}
                  onChange={handleInputChange}
                  required
                  pattern="[a-zA-Z0-9_-]+"
                  title="Only letters, numbers, hyphens, and underscores are allowed"
                />
                <small>This will be used in the URL: /p/your-slug</small>
              </div>
              
              <div className="form-group">
                <label htmlFor="title">Title:</label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="content">Content (HTML supported):</label>
                <textarea
                  id="content"
                  name="content"
                  value={formData.content}
                  onChange={handleInputChange}
                  rows="10"
                  required
                ></textarea>
              </div>
              
              <div className="form-group checkbox">
                <input
                  type="checkbox"
                  id="is_published"
                  name="is_published"
                  checked={formData.is_published}
                  onChange={handleInputChange}
                />
                <label htmlFor="is_published">Publish page</label>
              </div>
              
              <div className="form-actions">
                <button type="button" onClick={() => setActiveTab('list')}>Cancel</button>
                <button type="submit" className="primary">Create Page</button>
              </div>
            </form>
          </div>
        )}

        {activeTab === 'edit' && editingPage && (
          <div className="page-form">
            <h2>Edit Page</h2>
            <form onSubmit={handleUpdatePage}>
              <div className="form-group">
                <label htmlFor="slug">Slug (URL path):</label>
                <input
                  type="text"
                  id="slug"
                  name="slug"
                  value={formData.slug}
                  disabled
                />
                <small>Slug cannot be changed after creation</small>
              </div>
              
              <div className="form-group">
                <label htmlFor="title">Title:</label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="content">Content (HTML supported):</label>
                <textarea
                  id="content"
                  name="content"
                  value={formData.content}
                  onChange={handleInputChange}
                  rows="10"
                  required
                ></textarea>
              </div>
              
              <div className="form-group checkbox">
                <input
                  type="checkbox"
                  id="is_published"
                  name="is_published"
                  checked={formData.is_published}
                  onChange={handleInputChange}
                />
                <label htmlFor="is_published">Publish page</label>
              </div>
              
              <div className="form-actions">
                <button type="button" onClick={() => setActiveTab('list')}>Cancel</button>
                <button type="submit" className="primary">Update Page</button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminPage;
